import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Milestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

const milestones: Milestone[] = [
{
  year: '1995-1998',
  title: 'Fastest Man of India',
  description: 'Broke the national record in 100m sprint with a timing of 10.54 seconds, defining his journey as an elite athlete.',
  icon: 'TrophyIcon'
},
{
  year: '1998-2015',
  title: 'Two Comebacks & Reflection',
  description: 'After his second comeback, a deeper thought emerged—how to give back to the sport and pass on knowledge to the next generation.',
  icon: 'FireIcon'
},
{
  year: '2016',
  title: 'GO Speed Sprint Academy Founded',
  description: 'Dream became reality: train young athletes to become strong, confident, and world-class competitors through discipline and belief.',
  icon: 'AcademicCapIcon'
},
{
  year: '2016-2026',
  title: 'Building Champions & Family',
  description: 'Created a family built on passion, discipline, and belief that every child has the potential to shine—not just train, but grow.',
  icon: 'UserGroupIcon'
}];


const FounderStory = () => {
  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          <div className="order-2 lg:order-1">
            <div className="inline-block px-4 py-2 bg-accent/10 rounded-full mb-6">
              <span className="text-accent font-heading font-semibold text-sm">Founder's Journey</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-primary mb-6 leading-tight">
              The Champion Who Chose to Build Champions
            </h2>
            <div className="space-y-4 text-text-secondary font-body leading-relaxed">
              <p>
                From 1995 to 1998, Amit Khanna was known as the <span className="font-semibold text-primary">Fastest Man of India</span>. During this time, he broke the national record in the 100-metre sprint with a timing of <span className="font-semibold text-primary">10.54 seconds</span>—a moment that defined his journey as an athlete.
              </p>
              <p>
                But for him, success on the track was never the end goal.
              </p>
              <p>
                After his second comeback, a deeper thought stayed with him—how could he give back to the sport that gave him everything? He believed that real success lies in passing on knowledge, discipline, and belief to the next generation. His dream was simple yet powerful: to train young athletes to become strong, confident, and world-class competitors.
              </p>
              <p>
                This dream led to the birth of <span className="font-semibold text-primary">Go Speed Sprint Academy in 2016</span>.
              </p>
              <p>
                Mr. Khanna has always loved being around children. With his energetic and friendly nature, he connects easily with young athletes, understands their struggles, and pushes them to do better—while always making them feel supported. Discipline, consistency, and respect form the heart of his coaching, but so does encouragement, care, and joy.
              </p>
              <p className="text-primary font-semibold">
                Under his guidance, children don't just train—they grow. They gain confidence, learn life lessons, and slowly begin to believe in themselves. Go Speed is not just an academy; it is a family built on passion, discipline, and the belief that every child has the potential to shine.
              </p>
            </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden shadow-modal">
              <AppImage
                src="/assets/images/PHOTO-2024-08-14-08-16-14-1769030926924.jpg"
                alt="Amit Khanna, Fastest Man of India (1995-1998), in professional athletic gear on the track demonstrating coaching techniques"
                className="w-full h-[500px] object-cover" />

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <p className="text-white font-heading font-semibold text-lg">
                  "Real success lies in passing on knowledge, discipline, and belief to the next generation."
                </p>
                <p className="text-white/80 text-sm mt-2">— Amit Khanna, Founder, GO Speed Sprint Academy</p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-heading font-bold text-primary mb-4">
              Journey Milestones
            </h3>
            <p className="text-text-secondary font-body max-w-2xl mx-auto">
              From championship athlete to community builder – the evolution of a legacy
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {milestones.map((milestone, index) =>
            <div
              key={index}
              className="bg-card rounded-xl p-6 shadow-card hover:shadow-hover transition-all duration-300 border border-border">

                <div className="flex items-center justify-center w-12 h-12 bg-accent/10 rounded-lg mb-4">
                  <Icon name={milestone.icon as any} size={24} className="text-accent" />
                </div>
                <div className="text-accent font-heading font-bold text-lg mb-2">
                  {milestone.year}
                </div>
                <h4 className="text-primary font-heading font-semibold text-base mb-3">
                  {milestone.title}
                </h4>
                <p className="text-text-secondary text-sm font-body leading-relaxed">
                  {milestone.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default FounderStory;